import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ContentWrapper from "../../../common/components/ContentWrapper";
import PageBanner from "../../../common/components/PageBanner";
import BusinessModelForm from "../Home/components/BusinessModelForm";
import GovermentModalForm from "../Home/components/GovermentModalForm";
// The inquiry form popups are styled in the homepage carousel stylesheet.
import "../Home/components/CarouselSlider.css";

import BUSINESS_PHOTO from "../../../assets/new-images/business.png";
import constants from "../../../config/config";

// Business explainer video (see config.js). Set to null there to hide the video section.
const BUSINESS_VIDEO_URL = constants.BUSINESS_VIDEO_URL;

const BUSINESS_EMAIL = "info@myredfolder.com";

const benefits = [
  {
    title: "For Your Employees",
    tone: "employees",
    lead: "Extend your organization's commitment to employee well-being beyond the workplace.",
    intro: "Give participating employees access to practical tools that help them:",
    points: [
      "Prepare for emergencies",
      "Organize essential information",
      "Develop personalized Emergency Action Plans for themselves and their families",
    ],
    icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  },
  {
    title: "For Your Customers",
    tone: "customers",
    lead: "Strengthen customer relationships by offering a benefit that extends beyond the initial transaction.",
    intro:
      "MYREDFOLDER® provides an opportunity to offer meaningful preparedness resources that can remain valuable long after a purchase or service interaction. Whether your organization serves:",
    points: ["Homeowners", "Policyholders", "Retail customers", "Other communities"],
    chips: true,
    icon: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.7-7.7 1.1-1.1a5.5 5.5 0 0 0 0-7.8z",
  },
];

const privateItems = ["Private emergency plans", "Medical information", "Family communications", "Important documents"];

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const BusinessCommunity = () => {
  const [params] = useSearchParams();
  const [openForm, setOpenForm] = useState(null);
  const close = () => setOpenForm(null);

  // /business?type=community (from the homepage card) goes straight to that form.
  useEffect(() => {
    if (params.get("type") === "community") setOpenForm("community");
  }, [params]);

  return (
    <div>
      <PageBanner
        image={BUSINESS_PHOTO}
        alt="A diverse group of employees from different professions smiling together."
        eyebrow="For organizations"
        title="Business & Community"
        fade
      />

      <section className="mrf-section mrf-story" aria-labelledby="business-intro-heading">
        <div className="mrf-container" style={{ maxWidth: 1400 }}>
          <div className="mrf-story__top">
            <div className="mrf-story__intro">
              <h2 id="business-intro-heading" className="mrf-heading">
                Your Employees Take Care of Your Business. Help Them Take Care of Their Families.
              </h2>
              <p className="mrf-story__lead">
                A meaningful employee benefit. A valuable customer benefit. A commitment that extends beyond the
                workplace.
              </p>
              <p className="mrf-story__text">
                Every organization depends on people. Behind every employee is a family, and behind every customer is a
                life filled with responsibilities, unexpected challenges and people who matter.
              </p>
              <p className="mrf-story__text">When emergencies happen, those responsibilities don't disappear.</p>
            </div>
            <p className="mrf-story__question">
              What if your organization could help employees and customers prepare for life's unexpected emergencies
              before they occur?
            </p>
          </div>

          <div className="mrf-story__cols">
            <div className="mrf-story__col">
              <h3 className="mrf-story__subhead">The opportunity</h3>
              <p className="mrf-story__text">
                MYREDFOLDER® offers businesses of all sizes an opportunity to provide a comprehensive family emergency
                preparedness, response and recovery platform as an employee benefit or customer value-added service.
              </p>
            </div>
            <div className="mrf-story__col">
              <h3 className="mrf-story__subhead">How it works</h3>
              <p className="mrf-story__text">
                Through a customizable, white-labeled partnership, your organization can offer a branded preparedness
                program that helps participating households:
              </p>
              <ul className="mrf-story__list">
                <li>Establish emergency plans</li>
                <li>Organize important information</li>
                <li>Prepare for relevant hazards</li>
                <li>Coordinate with family members when emergencies occur</li>
              </ul>
            </div>
            <div className="mrf-story__col">
              <h3 className="mrf-story__subhead">Why it matters</h3>
              <p className="mrf-story__text">
                It's more than another employee benefit. It's an opportunity to demonstrate that your organization
                values the well-being of the people who contribute to its success.
              </p>
            </div>
          </div>
        </div>

        <p className="mrf-story__band">
          <span>Your Brand.</span> <span>Our Platform.</span> <span>Their Preparedness.</span>
        </p>
      </section>

      {BUSINESS_VIDEO_URL && (
        <section className="mrf-section mrf-section--alt" aria-labelledby="business-video-heading">
          <div className="mrf-container" style={{ maxWidth: 1100 }}>
            <h2 id="business-video-heading" className="mrf-heading mrf-heading--center">
              See How MYREDFOLDER® Can Work for Your Business
            </h2>
            <div className="mrf-video mrf-video--framed">
              <iframe
                src={BUSINESS_VIDEO_URL}
                title="How MYREDFOLDER® can work for your business (video)"
                loading="lazy"
                allow="fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      <section className="mrf-section" aria-labelledby="business-benefits-heading">
        <div className="mrf-container" style={{ maxWidth: 1200 }}>
          <h2 id="business-benefits-heading" className="mrf-heading mrf-heading--center">
            One Partnership. Two Opportunities.
          </h2>
          <div className="mrf-bene__grid">
            {benefits.map((b) => (
              <article key={b.title} className={`mrf-bene__card mrf-bene__card--${b.tone}`}>
                <div className="mrf-bene__head">
                  <span className="mrf-bene__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d={b.icon} />
                    </svg>
                  </span>
                  <h3 className="mrf-bene__title">{b.title}</h3>
                </div>
                <p className="mrf-bene__lead">{b.lead}</p>
                <p className="mrf-bene__text">{b.intro}</p>
                {b.chips ? (
                  <ul className="mrf-bene__chips">
                    {b.points.map((pt) => (
                      <li key={pt}>{pt}</li>
                    ))}
                  </ul>
                ) : (
                  <ul className="mrf-bene__list">
                    {b.points.map((pt) => (
                      <li key={pt}>{pt}</li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mrf-section mrf-section--alt" aria-labelledby="business-privacy-heading">
        <div className="mrf-container" style={{ maxWidth: 1200 }}>
          <div className="mrf-privacy">
            <div className="mrf-privacy__intro">
              <h2 id="business-privacy-heading" className="mrf-heading">
                Your Business Sponsors the Benefit. Families Control Their Plans.
              </h2>
              <p className="mrf-privacy__lead">
                MYREDFOLDER® is designed to distinguish the business-sponsored program from each participating
                household's personal account.
              </p>
              <dl className="mrf-privacy__roles">
                <div className="mrf-privacy__role">
                  <dt>Your organization</dt>
                  <dd>Sponsors the program and offers the benefit to employees or customers.</dd>
                </div>
                <div className="mrf-privacy__role mrf-privacy__role--household">
                  <dt>Each household</dt>
                  <dd>Has its own personal account and controls its own plans.</dd>
                </div>
              </dl>
              <p className="mrf-callout mrf-privacy__callout">
                It's a partnership designed to support preparedness while respecting personal privacy.
              </p>
            </div>

            <div className="mrf-privacy__card">
              <h3 className="mrf-privacy__card-title">Stays with each household</h3>
              <ul className="mrf-privacy__items">
                {privateItems.map((item) => (
                  <li key={item} className="mrf-privacy__item">
                    <span className="mrf-privacy__item-icon"><LockIcon /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mrf-privacy__card-note">Not automatically shared with your organization.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mrf-final" aria-labelledby="business-inquiry-heading">
        <div className="mrf-container" style={{ maxWidth: 1400 }}>
          <div className="mrf-final__grid">
            <div className="mrf-final__copy">
              <span className="mrf-final__eyebrow">Next step</span>
              <h2 id="business-inquiry-heading" className="mrf-final__title">
                Make Preparedness Part of Your Organization's Commitment to People.
              </h2>
              <p className="mrf-final__promise">Give them another reason to know that their well-being matters.</p>
              <p className="mrf-final__text">
                Your employees help take care of your business. Your customers place their trust in your organization.
              </p>
              <p className="mrf-final__tagline">MYREDFOLDER® — Prepare Today. Protect Tomorrow.</p>
            </div>

            <div className="mrf-final__actions">
              <div className="mrf-final__card mrf-final__card--primary">
                <p className="mrf-final__step">
                  Explore how MYREDFOLDER® can become part of your employee benefits or customer engagement program.
                </p>
                <button type="button" className="mrf-btn mrf-btn--primary" onClick={() => setOpenForm("organization")}>
                  Explore a business partnership
                </button>
                <p className="mrf-final__note">
                  Complete the inquiry form or contact us at{" "}
                  <a className="mrf-link" href={`mailto:${BUSINESS_EMAIL}`}>{BUSINESS_EMAIL}</a>.
                </p>
              </div>
              <div className="mrf-final__card">
                <p className="mrf-final__card-title">Community organization, agency or house of worship?</p>
                <button type="button" className="mrf-btn mrf-btn--on-dark" onClick={() => setOpenForm("community")}>
                  Discuss a community deployment
                </button>
              </div>
              <p className="mrf-final__privacy">
                Please do not include passwords, medical records, or other sensitive information in these forms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {openForm === "organization" && <BusinessModelForm onOpen onClose={close} close={close} />}
      {openForm === "community" && <GovermentModalForm onOpen onClose={close} close={close} />}
    </div>
  );
};

const BusinessCommunityPage = () => <ContentWrapper contenview={<BusinessCommunity />} />;

export default BusinessCommunityPage;
